import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColumnDefinition } from "tabulator-tables";
import TabulatorRaw, { EventsType } from "../../components/TabulatorRaw";
import { TDate } from "../../TDate";
import AnalyticsFilter2 from "./AnalyticsFilter/AnalyticsFilter2";

const Analytics: React.FC = () => {
  const [tableReady, setTableReady] = useState(false);
  const [columns, setColumns] = useState<ColumnDefinition[]>([]);
  const [columnReady, setColumnReady] = useState(false);
  const [tableData, setTableData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tabulatorRef = useRef<any>(null);

  const columnsMap = useMemo<any>(
    () => [
      { field: "id", title: "ID", include: false },
      { field: "productId", title: "Product ID", include: false },
      { field: "firstName", title: "First Name", include: true, frozen: true },
      { field: "lastName", title: "Last Name", include: true, frozen: true },
      { field: "group", title: "Client", include: true, frozen: true },
      { field: "level", title: "Type", include: true, frozen: true },
      { field: "product", title: "Product", include: true, frozen: true },
      { field: "owner", title: "Owner", include: true, frozen: true },
      {
        field: "creationDate",
        title: "Created",
        include: true,
        hozAlign: "center",
        frozen: true,
      },
      {
        field: "lastUsage",
        title: "Last",
        include: true,
        hozAlign: "center",
        frozen: true,
      },
      {
        field: "days",
        title: "Days",
        include: true,
        hozAlign: "center",
        frozen: true,
      },
      { field: "creationTime", title: "Creation Time", include: false },
      { field: "email", title: "Email", include: true, frozen: true },
      { field: "dates", title: "Dates", include: false },
      { field: "Help", title: "Help", include: true, hozAlign: "center" },
      { field: "total", title: "Pages", include: true, hozAlign: "center" },
      {
        field: "Export Data",
        title: "Export Data",
        include: true,
        hozAlign: "center",
      },
      { field: "Rank", title: "Rank", include: true, hozAlign: "center" },
      { field: "SMS", title: "SMS", include: true, hozAlign: "center" },
      {
        field: "Portfolio",
        title: "Portfolio",
        include: true,
        hozAlign: "center",
      },
      {
        field: "Security",
        title: "Security",
        include: true,
        hozAlign: "center",
      },
      { field: "Alerts", title: "Alerts", include: true, hozAlign: "center" },
      { field: "Markets", title: "Markets", include: true, hozAlign: "center" },
      {
        field: "Screening",
        title: "Screening",
        include: true,
        hozAlign: "center",
      },
      { field: "Builder", title: "Builder", include: true, hozAlign: "center" },
      {
        field: "Report Generation",
        title: "Report Generation",
        include: true,
        hozAlign: "center",
      },
      {
        field: "Report Hub",
        title: "Report Hub",
        include: true,
        hozAlign: "center",
      },
    ],
    []
  );

  const handlerAnalyticsResponse = useCallback(
    (response: any) => {
      const data: any[] = response.tableByUser;

      data.forEach((item) => {
        // date conversion
        if (item?.lastUsage) {
          item.lastUsage = TDate.daysToIso8601(item.lastUsage);
        }
        item.creationDate = TDate.dateToIso8601(new Date(item.creationDate));
        //----------------
      });
      let _columns: any[] = [];
      data.forEach((element: any) => {
        const keys = Object.keys(element);
        keys.forEach((key) => {
          if (!_columns.includes(key)) {
            _columns.push(key);
          }
        });
      });
      let columnsToBeIncluded = columnsMap.filter(
        (item: any) => item.include === true
      );
      columnsToBeIncluded = columnsToBeIncluded.map((item: any) => item.field);

      columnsToBeIncluded.map((field: string) => {
        if (_columns.includes(field)) {
          return field;
        } else {
          return null;
        }
      });

      _columns = columnsToBeIncluded.map((column: any) => {
        for (let i = 0; i < columnsMap.length; i++) {
          if (columnsMap[i].field === column) {
            let _c: ColumnDefinition = {
              title: columnsMap[i].title ?? column,
              field: column,
              hozAlign: columnsMap[i].hozAlign ?? "left",
              vertAlign: "middle",
              formatter: columnsMap[i].formatter ?? null,
              frozen: columnsMap[i].frozen ?? false,
            };
            return _c;
          }
        }
        return null;
      });
      _columns = [
        {
          title: "",
          frozen: true,
          formatter: "rownum",
        },
        ..._columns,
      ];
      setColumns(_columns);
      setTableData(data);
    },
    [columnsMap]
  );

  const clearFiltersHandler = useCallback(() => {
    setTableData(null);
    setColumns([]);
  }, []);

  useEffect(() => {
    if (tableReady && columnReady) {
      if (tabulatorRef.current && tableData) {
        const table = tabulatorRef.current.getTableInstance();
        let data = table.getData();
        if (data.length <= 0 && table.initialized) {
          table.setData(tableData).then(() => {
            table.redraw();
            table.setSort("group", "desc");
          });
        }
      }
    }
  }, [columnReady, tableData, tableReady]);

  useEffect(() => {
    if (tableReady) {
      if (tabulatorRef.current && columns) {
        const table = tabulatorRef.current.getTableInstance();
        table.initialized && table.setColumns(columns);
        table.initialized && table.redraw();
      }
    }
  }, [columnReady, columns, tableReady]);

  const events: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: () => {
          setTableReady(true);
        },
      },
      {
        event: "tableDestroyed",
        callback: () => {
          setTableReady(false);
        },
      },
      { event: "columnsLoaded", callback: () => setColumnReady(true) },
    ],
    []
  );

  const tableOptions = useMemo(
    () => ({
      layout: "fitColumnsStretch",
    }),
    []
  );

  const onTableExport = useCallback(() => {
    if (tabulatorRef.current) {
      const table = tabulatorRef.current.getTableInstance();
      table.download("csv", "usage.csv");
    }
  }, []);

  return (
    <Box
      width={"100%"}
      overflow={"hidden"}
      fontSize={"0.7vw"}
      p={1}
      position={"relative"}
    >
      <Box display={"flex"} position={"relative"} gap={1} height={"100%"}>
        <Box
          boxShadow={3}
          p={1}
          borderRadius={2}
          width={"100%"}
          display={"flex"}
          maxWidth={"20%"}
          overflow={"auto"}
        >
          <AnalyticsFilter2
            setIsLoading={setIsLoading}
            onClearFilters={clearFiltersHandler}
            setData={handlerAnalyticsResponse}
          />
        </Box>

        <Box
          width={"100%"}
          overflow={"hidden"}
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          boxShadow={3}
          p={1}
          borderRadius={2}
        >
          {!isLoading && tableData != null && (
            <Box width={"100%"} display={"flex"} justifyContent={"flex-end"}>
              <Button
                onClick={onTableExport}
                color="secondary"
                size="small"
                endIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
            </Box>
          )}
          {!isLoading && tableData && tableReady != null && (
            <TabulatorRaw
              ref={tabulatorRef}
              events={events}
              options={tableOptions}
            />
          )}

          {isLoading && (
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              justifyContent={"center"}
              height={"100%"}
              gap={1}
            >
              <CircularProgress color="secondary" />
              <Typography>Loading usage data...</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Analytics;
